import React, { useEffect, useMemo, useRef } from "react";

/**
 * ConstellationBackground
 * - Lightweight canvas-based animated constellation for login page branding.
 * - Pauses when tab is hidden, respects prefers-reduced-motion, DPR-aware.
 * - Uses HEAT brand orange colors.
 */
export default function ConstellationBackground({
  density = 0.00008, // nodes per pixel (auto-scales with area)
  maxNodes = 140,
  minNodes = 55,
  maxLinkDist = 140, // px (pre-DPR logical px)
  nodeRadius = [0.7, 1.8] as [number, number], // min/max
  speed = 0.20, // base drift speed
  opacity = 0.8,
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const runningRef = useRef(true);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let dpr = 1;

    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
    const rand = (min: number, max: number) => min + Math.random() * (max - min);

    // HEAT Brand Colors
    const colors = {
      line: { r: 234, g: 88, b: 12 },      // #ea580c - HEAT Orange
      glow: { r: 234, g: 88, b: 12 },      // #ea580c - HEAT Orange
      core: { r: 249, g: 115, b: 22 }      // #f97316 - Light HEAT Orange
    };

    // Mouse / parallax state
    const mouse = { x: 0, y: 0, has: false };

    // Nodes
    interface Node {
      x: number;
      y: number;
      vx: number;
      vy: number;
      r: number;
      phase: number;
      tw: number;
    }
    let nodes: Node[] = [];

    const makeNodes = () => {
      const area = width * height; // logical px
      const target = clamp(Math.floor(area * density), minNodes, maxNodes);

      nodes = new Array(target).fill(null).map(() => {
        const r = rand(nodeRadius[0], nodeRadius[1]);
        const vx = rand(-1, 1) * speed;
        const vy = rand(-1, 1) * speed;
        const phase = rand(0, Math.PI * 2);
        return {
          x: rand(0, width),
          y: rand(0, height),
          vx,
          vy,
          r,
          phase,
          tw: rand(0.6, 1.4), // twinkle speed
        };
      });
    };

    const resize = () => {
      const parent = canvas.parentElement;
      const rect = parent ? parent.getBoundingClientRect() : canvas.getBoundingClientRect();

      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);

      dpr = Math.min(2, window.devicePixelRatio || 1); // cap for perf
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // draw in logical px

      makeNodes();
    };

    // Rendering helpers (dark + HEAT orange glow)
    const draw = (t: number) => {
      // Background is handled by page CSS; canvas stays transparent
      ctx.clearRect(0, 0, width, height);

      // Subtle vignette (optional, very light)
      const grad = ctx.createRadialGradient(
        width * 0.5,
        height * 0.45,
        Math.min(width, height) * 0.10,
        width * 0.5,
        height * 0.5,
        Math.min(width, height) * 0.80
      );
      grad.addColorStop(0, `rgba(255, 255, 255, ${0.02 * opacity})`);
      grad.addColorStop(1, `rgba(0, 0, 0, 0)`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      // Parallax offset (gentle)
      const px = mouse.has ? (mouse.x / width - 0.5) : 0;
      const py = mouse.has ? (mouse.y / height - 0.5) : 0;
      const parallaxX = px * 10;
      const parallaxY = py * 10;

      // Draw links - HEAT Orange
      ctx.lineWidth = 1;

      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          if (dist > maxLinkDist) continue;

          const k = 1 - dist / maxLinkDist;

          // HEAT orange line
          ctx.strokeStyle = `rgba(${colors.line.r}, ${colors.line.g}, ${colors.line.b}, ${0.18 * k * opacity})`;
          ctx.beginPath();
          ctx.moveTo(a.x + parallaxX * 0.6, a.y + parallaxY * 0.6);
          ctx.lineTo(b.x + parallaxX * 0.6, b.y + parallaxY * 0.6);
          ctx.stroke();
        }
      }

      // Draw nodes - HEAT Orange
      for (const n of nodes) {
        const twinkle = 0.65 + 0.35 * Math.sin(n.phase + t * 0.001 * n.tw);
        const r = n.r * twinkle;

        // Glow
        ctx.beginPath();
        ctx.fillStyle = `rgba(${colors.glow.r}, ${colors.glow.g}, ${colors.glow.b}, ${0.10 * opacity})`;
        ctx.arc(n.x + parallaxX, n.y + parallaxY, r * 4.2, 0, Math.PI * 2);
        ctx.fill();

        // Core point
        ctx.beginPath();
        ctx.fillStyle = `rgba(${colors.core.r}, ${colors.core.g}, ${colors.core.b}, ${0.55 * opacity})`;
        ctx.arc(n.x + parallaxX, n.y + parallaxY, r, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const step = (t: number) => {
      if (!runningRef.current) return;

      if (!prefersReducedMotion) {
        // Update node positions
        for (const n of nodes) {
          n.x += n.vx;
          n.y += n.vy;

          // Wrap edges softly
          if (n.x < -20) n.x = width + 20;
          if (n.x > width + 20) n.x = -20;
          if (n.y < -20) n.y = height + 20;
          if (n.y > height + 20) n.y = -20;
        }
      }

      draw(t);
      rafRef.current = requestAnimationFrame(step);
    };

    const onMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.has = true;
    };

    const onMouseLeave = () => {
      mouse.has = false;
    };

    const onVisibility = () => {
      const hidden = document.hidden;
      runningRef.current = !hidden;

      if (!hidden) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(step);
      } else {
        cancelAnimationFrame(rafRef.current);
      }
    };

    const ro = new ResizeObserver(() => resize());

    // Init
    resize();
    ro.observe(canvas.parentElement || canvas);

    canvas.addEventListener("mousemove", onMouseMove, { passive: true });
    canvas.addEventListener("mouseleave", onMouseLeave, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);

    // Start
    runningRef.current = true;
    rafRef.current = requestAnimationFrame(step);

    return () => {
      runningRef.current = false;
      cancelAnimationFrame(rafRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseleave", onMouseLeave);
      ro.disconnect();
    };
  }, [
    density,
    maxNodes,
    minNodes,
    maxLinkDist,
    nodeRadius,
    speed,
    opacity,
    prefersReducedMotion,
  ]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
}
